// pages/user/SellDevice.jsx
import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import dayjs from "dayjs";
import { FiSun, FiMoon, FiPhone } from "react-icons/fi";
import { Input } from "antd";
import toast from "react-hot-toast";
import { useSEO } from "../../hooks/useSEO";
import { useTheme } from "../../theme/ThemeProvider";
import {
  REQUEST_CONFIRMATION_ROUTE,
  SELL_DEVICE_ROUTE,
} from "../../routes/constant";
import {
  validateEvaluation,
  createServiceRequest,
  clearSellDevice,
  setSelectedAddress,
  removeCouponFromEvaluation,
  applyCouponToEvaluation,
} from "../../store/slices/sellDeviceSlice";
import Layout from "../../components/Layout";
import SellDeviceSkeleton from "../../components/user/lead/sell-device/SellDeviceSkeleton";
import ErrorState from "../../components/user/lead/sell-device/ErrorState";
import ProductHeader from "../../components/user/lead/sell-device/ProductHeader";
import AddressForm from "../../components/user/lead/sell-device/AddressForm";
import PincodeUnavailableState from "../../components/state/PincodeUnavailableState";
import PickupOptions from "../../components/user/lead/sell-device/PickupOptions";
import TermsAndSubmit from "../../components/user/lead/sell-device/TermsAndSubmit";
import { pickupValidationSchema } from "../../validations/pickupValidation";
import CouponInput from "../../components/user/lead/sell-device/CouponInput";
import OptionalLocation from "../../components/user/lead/sell-device/OptionalLocation";
import { api } from "../../services/api";
import { pincodeLookupRejection, unavailablePincode, validPincodeLookup, TAP2CASH_APP_URL } from "../../utils/pincodeAvailability";
import { getNavigationId, queueSellFormOpen } from "../../utils/apiAttribution";

const SellDevice = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { variantSlug, productSlug, brandSlug } = useParams();

  const [isSellFormActive, setIsSellFormActive] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [unavailable, setUnavailable] = useState(null);
  const recentlySavedAddress = useRef(null);
  const { addresses } = useSelector((state) => state.sellDevice);

  // Form visibility changes locally; record its opening immediately.
  // A queued copy survives a failed request and can be flushed on page exit.
  const handleSellNow = () => {
    const signal = queueSellFormOpen(window.location.href, getNavigationId());
    setIsSellFormActive(true);
    if (signal) {
      api.recordSellFormOpened(signal).catch(() => {
        // Marketing delivery failures must never prevent selling the device.
      });
    }
  };

  const {
    evaluationValid,
    evaluationResponse,
    evaluationError,
    productData,
    validationLoading,
    submitting,
    couponApplied,
    couponCode,
    couponBonusAmount,
    applyingCoupon,
    removingCoupon,
  } = useSelector((state) => state.sellDevice);

  const categoryId = useMemo(() => {
    const product = evaluationResponse?.variation?.product || productData?.variation?.product;
    return product?.category_id || product?.category?.id;
  }, [evaluationResponse, productData]);
  const brandId = useMemo(() => {
    const product = evaluationResponse?.variation?.product || productData?.variation?.product;
    return product?.brand_id || product?.brand?.id;
  }, [evaluationResponse, productData]);

  const dateOptions = [];
  for (let i = 1; i <= 4; i++) {
    const date = dayjs().add(i, "day");
    dateOptions.push({
      value: date.format("YYYY-MM-DD"),
      label: date.format("dddd, DD MMM"),
    });
  }

  const timeSlots = [
    {
      value: "10:00-14:00",
      label: "10:00 AM - 2:00 PM",
      icon: <FiSun size={12} />,
    },
    {
      value: "14:00-20:00",
      label: "2:00 PM - 8:00 PM",
      icon: <FiMoon size={12} />,
    },
  ];

  const handleApplyCoupon = async (couponCode) => {
    try {
      const result = await dispatch(
        applyCouponToEvaluation({
          brandSlug: brandSlug,
          productSlug: productSlug,
          variationSlug: variantSlug,
          couponCode: couponCode,
        }),
      );
      if (applyCouponToEvaluation.fulfilled.match(result)) {
        toast.success("Coupon applied successfully!");
        return { success: true };
      } else {
        return {
          success: false,
          message: result.payload || "Failed to apply coupon",
        };
      }
    } catch (error) {
      return { success: false, message: "Failed to apply coupon" };
    }
  };

  const handleRemoveCoupon = async () => {
    try {
      const result = await dispatch(
        removeCouponFromEvaluation({
          brandSlug: brandSlug,
          productSlug: productSlug,
          variationSlug: variantSlug,
        }),
      );
      if (removeCouponFromEvaluation.fulfilled.match(result)) {
        toast.success("Coupon removed successfully!");
        return { success: true };
      } else {
        return {
          success: false,
          message: result.payload || "Failed to remove coupon",
        };
      }
    } catch (error) {
      return { success: false, message: "Failed to remove coupon" };
    }
  };

  const formik = useFormik({
    initialValues: {
      addressId: null,
      alternateMobile: "",
      isExpress: false,
      opd: null,
      slot: null,
      agreed: true,
      location: null,
    },
    validationSchema: pickupValidationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      if (isSubmitting) return;
      setIsSubmitting(true);
      setUnavailable(null);

      try {
        const saved = addresses.find((address) => String(address.id) === String(values.addressId));
        const recent = recentlySavedAddress.current;
        const pincode = saved?.pincode || (String(recent?.id) === String(values.addressId) ? recent.pincode : null);
        if (!pincode) {
          toast.error("Select a pickup address before proceeding.");
          return;
        }
        try {
          const locationResponse = await api.getPincodeAddress(pincode, categoryId, brandId);
          if (!validPincodeLookup(locationResponse, pincode)) {
            setUnavailable(unavailablePincode(pincode));
            return;
          }
        } catch (error) {
          const rejected = pincodeLookupRejection(error, pincode);
          if (rejected) setUnavailable(rejected);
          else toast.error("Could not verify pickup availability. Please try again.");
          return;
        }
        const result = await dispatch(
          createServiceRequest({
            brandSlug: brandSlug,
            productSlug: productSlug,
            variationSlug: variantSlug,
            addressId: values.addressId,
            alternateMobile: values.alternateMobile,
            consentAccepted: values.agreed,
            isExpressPickup: values.isExpress,
            slot: values.isExpress ? null : values.slot,
            opd: values.isExpress ? null : values.opd,
            spd: values.isExpress ? null : values.opd,
            // Keep coordinates in the API's optional `location` object.
            // Do not send location when the customer has not shared it.
            location: values.location,
          }),
        );

        if (createServiceRequest.fulfilled.match(result)) {
          const serviceData = result.payload?.data || result.payload || {};
          const serviceRequestId =
            serviceData?.service_id ?? serviceData?.id ?? null;
          toast.success("Service request created successfully!");

          // Navigate to confirmation page with state ONLY (no localStorage)
          navigate(REQUEST_CONFIRMATION_ROUTE, {
            state: {
              ...serviceData,
              _tracking_service_request_id: serviceRequestId,
            },
          });
        } else if (result.payload) {
          if (result.payload.scopeRejected ||
              (result.payload.code === "NOT_FOUND" && result.payload.message?.includes("not operating in this area"))) {
            setUnavailable(unavailablePincode(pincode, result.payload.message, result.payload.downloadAppUrl || TAP2CASH_APP_URL));
          } else {
            toast.error(result.payload.message || "Failed to create service request");
          }
        }
      } catch (error) {
        toast.error(error?.message || "Something went wrong");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  // Build product data for ProductHeader
  const getProductHeaderData = () => {
    const data = evaluationResponse || productData || {};
    const variation = data.variation || {};
    const product = variation.product || {};

    // Get coupon data from response
    const couponData = data.coupon || {};

    return {
      id: data.id,
      evaluated_amount: data.evaluated_amount || 0,
      variation: {
        id: variation.id,
        title: variation.title || variantSlug?.replace(/-/g, " ") || "Product",
        slug: variation.slug || variantSlug,
      },
      product: {
        id: product.id,
        title: product.title || variantSlug?.replace(/-/g, " ") || "Product",
        image: product.image,
        category: product.category || null,
      },
      // Add coupon data
      coupon_code: couponData.code || data.coupon_code || null,
      coupon_bonus_amount:
        couponData.bonus_amount || data.coupon_bonus_amount || 0,
      coupon_bonus_type:
        couponData.bonus_type || data.coupon_bonus_type || null,
      has_coupon_applied:
        couponData.is_applied || data.has_coupon_applied || false,
      coupon_applied_at:
        couponData.applied_at || data.coupon_applied_at || null,
    };
  };

  const productHeaderData = getProductHeaderData();

  // ============= SEO - Only after data loads =============
  const seoParams = useMemo(() => {
    if (validationLoading || !productData) return null;

    const variationName =
      productHeaderData?.variation?.title ||
      variantSlug?.replace(/-/g, " ") ||
      "";
    const productName = productHeaderData?.product?.title || "";
    const amount = productHeaderData?.evaluated_amount || 0;

    return {
      variantSlug: variantSlug,
      variantName: variationName,
      productName: productName,
      amount: amount,
    };
  }, [validationLoading, productData, productHeaderData, variantSlug]);

  useSEO(SELL_DEVICE_ROUTE(variantSlug), seoParams);

  // Validate evaluation on mount - only needs variationSlug
  useEffect(() => {
    if (variantSlug) {
      dispatch(
        validateEvaluation({
          brandSlug: brandSlug,
          productSlug: productSlug,
          variationSlug: variantSlug,
        }),
      );
    } else {
      toast.error("Missing variation information");
    }
    return () => {
      dispatch(clearSellDevice());
    };
  }, [dispatch, variantSlug]);

  // Show loading state
  if (validationLoading || (!evaluationValid && !evaluationError)) {
    return (
      <Layout>
        <div className="px-4 py-6 mx-auto max-w-7xl">
          <SellDeviceSkeleton isDarkMode={isDarkMode} />
        </div>
      </Layout>
    );
  }

  // Show error state
  if (!evaluationValid && evaluationError) {
    return (
      <Layout>
        <ErrorState
          message={evaluationError}
          isDarkMode={isDarkMode}
          onGoBack={() => navigate(-1)}
        />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="px-4 py-6 mx-auto max-w-7xl">
        <div
          className={`rounded-xl overflow-hidden shadow-sm ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
        >
          {unavailable ? (
            <PincodeUnavailableState
              pincode={unavailable.pincode}
              message={unavailable.message}
              downloadAppUrl={unavailable.downloadAppUrl}
              isDarkMode={isDarkMode}
              onRetry={() => {
                setUnavailable(null);
                recentlySavedAddress.current = null;
                dispatch(setSelectedAddress(null));
                formik.setFieldValue("addressId", null);
              }}
            />
          ) : isSellFormActive ? (
            <div className="p-5 space-y-5">
              <CouponInput
                isCouponApplied={couponApplied}
                couponCode={couponCode}
                couponBonusAmount={couponBonusAmount}
                applyingCoupon={applyingCoupon}
                removingCoupon={removingCoupon}
                onApplyCoupon={handleApplyCoupon}
                onRemoveCoupon={handleRemoveCoupon}
                isDarkMode={isDarkMode}
                disabled={submitting || isSubmitting}
              />
              <PickupOptions
                formik={formik}
                dateOptions={dateOptions}
                timeSlots={timeSlots}
                isDarkMode={isDarkMode}
              />

              {/* Alternate Mobile Field */}
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                >
                  <FiPhone className="inline mr-2" size={14} />
                  Alternate Mobile Number (Optional)
                </label>
                <Input
                  placeholder="Enter alternate mobile number"
                  maxLength={10}
                  value={formik.values.alternateMobile}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value && !/^\d*$/.test(value)) return;
                    formik.setFieldValue("alternateMobile", value);
                  }}
                  className="rounded-lg"
                  prefix={<FiPhone className="text-gray-400" />}
                  size="large"
                  status={
                    formik.touched.alternateMobile &&
                    formik.errors.alternateMobile
                      ? "error"
                      : ""
                  }
                />
                {formik.touched.alternateMobile &&
                  formik.errors.alternateMobile && (
                    <div className="mt-2 text-xs text-red-500">
                      {formik.errors.alternateMobile}
                    </div>
                  )}
                <p
                  className={`text-xs mt-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                >
                  We'll use this number if we can't reach you on your registered
                  mobile
                </p>
              </div>

              <AddressForm
                categoryId={categoryId}
                brandId={brandId}
                onUnavailable={setUnavailable}
                formik={formik}
                isDarkMode={isDarkMode}
                onAddressSaved={async (addressId, pincode) => {
                  recentlySavedAddress.current = { id: addressId, pincode };
                  await formik.setFieldValue("addressId", addressId);
                  formik.setTouched({
                    addressId: true,
                    opd: true,
                    slot: true,
                    agreed: true,
                    alternateMobile: true,
                  });
                  const errors = await formik.validateForm();
                  if (Object.keys(errors).length === 0) {
                    formik.submitForm();
                  }
                }}
              />

              {formik.touched.addressId && formik.errors.addressId && (
                <div className="text-xs text-red-500">
                  {formik.errors.addressId}
                </div>
              )}

              <OptionalLocation
                location={formik.values.location}
                onChange={(loc) => formik.setFieldValue("location", loc)}
                isDarkMode={isDarkMode}
              />


              <TermsAndSubmit
                formik={formik}
                submitting={submitting || isSubmitting}
                onSubmit={() => formik.handleSubmit()}
                isDarkMode={isDarkMode}
              />
            </div>
          ) : (
            <ProductHeader
              onSale={handleSellNow}
              productData={productHeaderData}
              isDarkMode={isDarkMode}
            />
          )}
        </div>
      </div>
    </Layout>
  );
};

export default SellDevice;
